import React, { useCallback } from 'react';
import {
  Box,
  Checkbox,
  Grid,
  Hint,
  Inscription,
  ListItemField,
} from '@common-fe/common-fe';

import { HorizontalLine } from '@/components/elements';
import { ClaimMatchingType } from '@/modules/employer/components/Lockbox/lockbox.types';

import AllocateClaimAmount from './AllocateClaimAmount';
import ClaimMatchingMessage from './ClaimMatchingMessage';

const CardItem = ({ testId, title, value, node, style }: ListItemField & { testId?: string }) => {
  return (
    <Box data-tesId={`${testId}_cell`}>
      <Inscription
        data-tesId={`${testId}_title`}
        size="small"
        color="textSecondary"
        weight={500}
        lineHeight="18px"
      >
        {title}
      </Inscription>
      <Inscription
        data-tesId={`${testId}_value`}
        title={value}
        color="textBody"
        lineHeight="20px"
        style={{ 
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          ...style,
        }}
      >
        {value || node}
      </Inscription>
    </Box>
  );
};

interface Props {
  testId?: string;
  itemKey?: string;
  itemFields?: ListItemField[];
  isSelected?: boolean;
  onSelect?: (value?: string) => void;
  isEditMode?: boolean;
  showWarnings?: boolean;
  warnings?: ClaimMatchingType[];
  isSelectedFromAnotherPlace?: boolean;
}

const LockboxClaimCard: React.FC<Props> = ({
  testId,
  itemKey,
  isSelected,
  onSelect,
  itemFields,
  isEditMode,
  showWarnings,
  warnings,
  isSelectedFromAnotherPlace,
}) => {
  const handleSelect = useCallback(() => {
    if (onSelect && itemKey) {
      onSelect(itemKey);
    }
  }, [itemKey, onSelect]);

  return (
    <Box
      data-testid={`${testId}_claim-card`}
      background="canvas"
      round="container1Round"
      pad={{ vertical: 'spacing16', horizontal: 'spacing24' }}
      border={{ size: '1px', color: 'border2' }}
    >
      <Box direction="row">
        {isEditMode && (
          <Box direction="row" margin={{ vertical: 'spacing8', right: 'spacing12' }}>
            <Hint
              hideHint={!isSelectedFromAnotherPlace}
              hintAlign="vertical"
              hintWidth={154}
              hintElement={(
                <Checkbox
                  data-testId={`${testId}_claim-card`}
                  name={`${testId}_claim-card-checkbox`}
                  checked={isSelected}
                  onChange={handleSelect}
                  disabled={isSelectedFromAnotherPlace}
                />
              )}
            >
              <Inscription size="small" textAlign="center">
                This claim is already selected
              </Inscription>
            </Hint>
          </Box>
        )}

        <Grid
          width="100%"
          columns="1/4"
          gap={{
            column: 'spacing12',
            row: 'spacing8'
          }}
        >
          {itemFields?.map((cardItem) => (
            <CardItem
              testId={`${testId}_${cardItem.key}`}
              key={cardItem.key}
              type={cardItem.type}
              title={cardItem.title}
              value={cardItem.value}
              node={cardItem?.node}
              style={cardItem?.style}
            />
          ))}
        </Grid>
      </Box>
      {showWarnings && warnings?.map((warning) => (
        <Box key={warning} margin={{ top: 'spacing8' }}>
          <ClaimMatchingMessage matchingType={warning} />
        </Box>
      ))}
      <HorizontalLine margin={{ vertical: 'spacing8' }} />
      <AllocateClaimAmount
        claimId={itemKey}
        isSelected={isSelected}
        disabled={isSelectedFromAnotherPlace}
        isEditMode={isEditMode}
      />
    </Box>
  );
};

export default LockboxClaimCard;
